import { actions } from '@ecosystem/newron-design-system';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { EmptyState } from '../../common/no-data-warning/empty-state';
import { SelectionMenuComponent } from '../../common/selection.menu';
import { TitleSelectorButton } from '../../common/title.selector';
import { Instructions } from '../../content/instructions';
import GenericWarnings from '../../design/warning/GenericWarnings';
import { addTotalToMatchingSharedBrand, createChartRef } from '../utils';
import { useMobile } from '../../context/mobile/mobile.provider';
import classNames from 'classnames';
import { getExportContainer } from '../../export';
import { DataContentProps } from '../../newron-core';
import { useCorePageState } from '../../page/provider';
import { GroupedListBoxMenuItem, ListBoxMenuItem, ListMenuItem, Params } from '../../types';
import { useOnCollectionExportReady } from '../collection/useOnCollectionExportReady';
import { IndicatorCard } from './card';
import { makeIndicatorInfo } from './data';
import './indicators.css';
import { IndicatorConfig, IndicatorRow } from './types';
import { getBrandAndBrandOwner } from '../../common/brandOwner.utils';
import { updateOptionDisableState } from '../../page/utils';

function KeyIndicators({
  data,
  isLoading,
  instructions = {},
  componentSettings,
  updateSettings,
  componentName,
  collection,
}: DataContentProps) {
  const [dataStamp, setDataStamp] = useState<number>();
  const [info, setInfo] = useState<IndicatorRow[]>();
  const [selectOptions, setSelectOptions] = useState<ListMenuItem[]>([]);
  const [exportRefLength, setExportRefLength] = useState<number>(0);

  const { dsl, globalWarnings, contextParams, isCollections, pageWarnings, resetPageWarnings } = useCorePageState();
  const exportRef = useRef<HTMLDivElement[]>([]);
  const sharedBrands = dsl?.settings?.values?.sharedBrands || [];

  //filtered selected options based on data rows returned
  const [filteredSelectedOptions, setFilteredSelectedOptions] = useState<string[]>();

  useOnCollectionExportReady({ collection, dsl, chartSliceRefs: exportRef, componentName, refLength: exportRefLength });

  const myInstructions = new Instructions(instructions, componentSettings);
  const grouping = myInstructions.getString('grouping');
  const indicators = myInstructions.getObject('indicators') as IndicatorConfig[];
  let fixedItems =
    isCollections && grouping ? contextParams['highlightedBrand'] : myInstructions.getStringList('fixedItems');
  const limitedItems = myInstructions.getStringList('limitedItems');
  const limitedParamName =
    myInstructions.isFromSettings('limitedItems') && myInstructions.getSettingsName('limitedItems');
  const limitedOptions = myInstructions.getObject('limitedOptions') as ListMenuItem[];
  const addLinkLabel = myInstructions.getString('addLinkLabel', 'Add');
  const isAllowedToDisplayEmptyState = myInstructions.getBoolean('isAllowedToDisplayEmptyState', false);
  const showGroupLabel = limitedOptions?.[0]?.type === 'MenuItemGrouped';
  const canEditLimited = !!limitedParamName;

  const selectedFixedItems = fixedItems ?? [];
  const selectedLimitedItems = limitedItems ?? [];

  const baseList = limitedOptions || selectOptions;

  let toAddSelections: ListMenuItem[];

  if (canEditLimited && baseList) {
    toAddSelections = baseList;
  }

  useEffect(() => {
    if (data && data.stamp !== dataStamp) {
      const info = makeIndicatorInfo(data, {
        configs: indicators,
        grouping,
        fixedItems: selectedFixedItems,
        limitedItems: selectedLimitedItems,
      });

      setFilteredSelectedOptions(info.filteredLimitedItems);

      setInfo(info.rows);
      setSelectOptions(info.rowItems);
      setDataStamp(data.stamp);
    }
  }, [data, dataStamp]);

  useEffect(() => setDataStamp(0), [fixedItems, limitedItems]);

  useEffect(() => {
    if (data) collection?.onLoaded();
  }, [data]);

  const handleRowChange = (value: string, newValue: string) => {
    if (canEditLimited) {
      const updated = filteredSelectedOptions;
      updated.splice(
        updated.findIndex(item => {
          const { brand, brandOwner } = getBrandAndBrandOwner(item);
          return brand ? brand === value : brandOwner === value;
        }),
        1,
        newValue
      );
      updateSettings({ [limitedParamName]: updated });

      setFilteredSelectedOptions(updated);
      resetPageWarnings();
    }
  };

  const handleRowAdd = (value: Params) => {
    if (canEditLimited) {
      updateSettings({ [limitedParamName]: [...filteredSelectedOptions, ...value.add] });
      resetPageWarnings();
    }
  };

  const handleRowDelete = (value: string) => {
    if (canEditLimited) {
      const updatedList = filteredSelectedOptions.filter(item => {
        const { brand, brandOwner } = getBrandAndBrandOwner(item);
        return brand ? brand !== value : brandOwner !== value;
      });

      updateSettings({
        [limitedParamName]: updatedList,
      });

      setFilteredSelectedOptions(updatedList);
      resetPageWarnings();
    }
  };

  const ExportContainer = useMemo(() => getExportContainer(componentName), []);

  if (data && !data.hasDataResultSetsValues() && !data.hasDataInSomeGroupedSets()) {
    collection?.onExportReady(null);
    return isAllowedToDisplayEmptyState ? <EmptyState /> : null;
  }

  const renderLoadingIndicators = () => (
    <div className="flex items-stretch flex-nowrap gap-6 mb-6">
      {indicators.map((_, idx) => (
        <IndicatorCard key={`fake_${idx}`} isLoading />
      ))}
    </div>
  );

  // renders elements used in export
  const renderExportSlide = () => {
    if (!info) {
      return null;
    }
    return getChunkedIndicatorRow(info).map((rowItems, idx, arr) => renderSlide(rowItems, idx, arr.length));
  };

  const renderSlide = (rowItems: IndicatorRow[], idx: number, totalSlides: number) => {
    const refCallback = createSlideRefCallback(rowItems, idx, totalSlides);
    /** Here clone row items to avoid mutating state,
     * then update labels by appending '(total)' for shared brands
     */
    const clonedRowItems = structuredClone(rowItems);
    const rowItemsWithSharedBrandTotal = clonedRowItems.map(row => {
      const updatedRow = { ...row };
      updatedRow.label = addTotalToMatchingSharedBrand(row.value, row.label, sharedBrands);
      return updatedRow;
    });
    return (
      <div
        className={`${componentName}-export-slide w-screen flex flex-column justify-center gap-6`}
        key={`slides-${idx}`}
        ref={refCallback}
      >
        {globalWarnings && <GenericWarnings warnings={globalWarnings} />}
        {pageWarnings && <GenericWarnings warnings={pageWarnings} />}
        {rowItemsWithSharedBrandTotal.map(renderIndicatorRow)}
      </div>
    );
  };

  const createSlideRefCallback = (rowItems: IndicatorRow[], idx: number, totalSlides: number) => {
    return (el: HTMLDivElement | null) => {
      const hasRefIndex = info?.some(r => r.value === rowItems?.[0]?.value);
      if (hasRefIndex && el) {
        createChartRef(idx, totalSlides, setExportRefLength, exportRef)(el);
      }
    };
  };

  const renderIndicatorRow = (row: IndicatorRow) => {
    return (
      <div className="border border-light py-3 px-15 rounded-2 mx-20" key={`indicator_row_${row.value}`}>
        <IndicatorsRow key={`row_${row.value}`} row={row} isLoading={isLoading} selectionItems={[]} />
      </div>
    );
  };

  const getChunkedIndicatorRow = (rows: IndicatorRow[]) => {
    const firstChunkSize = 4;
    const otherChunkSize = 5;
    return rows.reduce((chunks: IndicatorRow[][], row, i) => {
      const chunkIndex = i < firstChunkSize ? 0 : 1 + Math.floor((i - firstChunkSize) / otherChunkSize);
      if (!chunks[chunkIndex]) chunks[chunkIndex] = [];
      chunks[chunkIndex].push(row);
      return chunks;
    }, []);
  };

  return (
    <>
      {!info
        ? renderLoadingIndicators()
        : info.map((row, idx) => (
            <IndicatorsRow
              key={`row_${idx}`}
              row={row}
              isLoading={isLoading}
              selectionItems={toAddSelections as ListBoxMenuItem[] | GroupedListBoxMenuItem[]}
              onChange={canEditLimited && !collection ? handleRowChange : undefined}
              onDelete={canEditLimited && !collection ? handleRowDelete : undefined}
              isClickable={true}
              showGroupLabel={showGroupLabel}
              selectedOptions={filteredSelectedOptions}
              sharedBrands={sharedBrands}
            />
          ))}
      {toAddSelections && !collection && info && (
        <SelectionMenuComponent
          key={info.map(row => row.value).join('_')}
          menuConfig={{
            paramName: 'add',
            items: updateOptionDisableState(
              filteredSelectedOptions,
              baseList as ListBoxMenuItem[] | GroupedListBoxMenuItem[]
            ),
          }}
          values={{}}
          onUpdate={updates => handleRowAdd(updates)}
          button={TitleSelectorButton}
          isSubMenuIndented={true}
          buttonLabel={addLinkLabel}
          showGroupLabel={showGroupLabel}
        />
      )}
      <ExportContainer>
        <div id={`${componentName}_export_content`}>
          {!info ? (
            <div className={`${componentName}-export-slide mx-3 p-5`}>{renderLoadingIndicators()}</div>
          ) : (
            renderExportSlide()
          )}
        </div>
      </ExportContainer>
    </>
  );
}

type RowProps = {
  row: IndicatorRow;
  selectionItems: ListBoxMenuItem[] | GroupedListBoxMenuItem[];
  isLoading: boolean;
  onChange?: (origValue: string, newValue: string) => void;
  onDelete?: (value: string) => void;
  isClickable?: boolean;
  showGroupLabel?: boolean;
  selectedOptions?: string[];
  sharedBrands?: string[];
};
function IndicatorsRow({
  row,
  selectionItems,
  isLoading,
  onChange,
  onDelete,
  isClickable,
  showGroupLabel,
  selectedOptions,
  sharedBrands,
}: Readonly<RowProps>) {
  const { indicators, isFixed, label, value } = row;
  const canEdit = !isFixed && onChange;

  const handleChange = (updates: Params) => {
    if (!onChange) {
      return;
    }
    const newValue = updates['row'][0];
    onChange(value, newValue);
  };

  const handleDelete = () => {
    if (!onDelete) {
      return;
    }
    onDelete(value);
  };

  const { IconButton } = actions;

  const { isMobile } = useMobile();

  return (
    <div className="relative">
      {row.value !== 'null' && canEdit && (
        <div className="pb-2 flex items-center gap-4">
          <SelectionMenuComponent
            menuConfig={{
              paramName: 'row',
              items: updateOptionDisableState(selectedOptions, selectionItems),
            }}
            values={{
              row: [value],
            }}
            onUpdate={handleChange}
            button={TitleSelectorButton}
            showGroupLabel={showGroupLabel}
            isSubMenuIndented={true}
          />
          {onDelete && (
            <IconButton onClick={handleDelete}>
              <IconButton.icons.Delete />
            </IconButton>
          )}
        </div>
      )}
      {value !== 'null' && !canEdit && (
        <div className={classNames('pb-2', { 'text-lg': !isMobile, 'text-md': isMobile })}>
          {addTotalToMatchingSharedBrand(value, label, sharedBrands)}
        </div>
      )}
      <div
        className={classNames('flex items-stretch gap-2', {
          'flex-nowrap overflow-x-auto hide-scrollbar': isMobile,
          'flex-nowrap': !isMobile,
        })}
      >
        {indicators.map((ind, idx) => (
          <IndicatorCard
            key={`ind_${idx}`}
            rowValue={row.value}
            info={ind}
            isLoading={isLoading}
            isClickable={isClickable}
          />
        ))}
      </div>
    </div>
  );
}

export { KeyIndicators };
